import { StoryModel } from '../../../story/models/story.model';
import { RenamespaceUtils } from './renamespace-utils';
import { mapKeys, mapValues } from 'lodash';
import { SceneGraphNode } from '../../models/scene-graph-node';
import { LabelModel } from '../../../story/models/labels/label.model';
import { ErrandInterface } from '../../../story/models/errands/errand.model';
import { ErrandType } from '../../../story/models/errands/errand-type.enum';
import { ChapterModel } from '../../../story/models/chapter.model';
import { VisibilityLayerModel } from 'src/app/story/models/visibility-layers.model';

function updateProps(dataWithProps, namespaceMap) {
  const showObjects = mapKeys(dataWithProps.showObjects, (value, key) =>
    RenamespaceUtils.renamespaceValue(key, namespaceMap),
  );
  const selectObjects = mapKeys(dataWithProps.selectObjects, (value, key) =>
    RenamespaceUtils.renamespaceValue(key, namespaceMap),
  );
  const pickThroughObjects = mapKeys(dataWithProps.pickThroughObjects, (value, key) =>
    RenamespaceUtils.renamespaceValue(key, namespaceMap),
  );
  const synchronization = mapKeys(dataWithProps.synchronization, (value, key) =>
    RenamespaceUtils.renamespaceValue(key, namespaceMap),
  );
  return {
    showObjects,
    selectObjects,
    pickThroughObjects,
    synchronization,
  };
}

function updateLabels(labels: LabelModel[], namespaceMap) {
  const updateErrands = (errands: ErrandInterface[]) => {
    return errands.map((errand) => {
      const renamespaceErrand =
        errand.type === ErrandType.flyToChapter || errand.type === ErrandType.flyToObject;

      if (!renamespaceErrand) {
        return errand;
      }

      return {
        ...errand,
        value: RenamespaceUtils.renamespaceValue(errand.value, namespaceMap),
      };
    });
  };

  return (labels || []).map((label) => {
    return {
      ...label,
      objectId: RenamespaceUtils.renamespaceValue(label.objectId, namespaceMap),
      states: label.states.map((state) => {
        return { ...state, errands: updateErrands(state.errands) };
      }),
    };
  });
}

function updateTimelineMap(timelineMaps, namespaceMap) {
  return (timelineMaps || []).map((tmap) => {
    tmap.data.timeline = namespaceMap[tmap.data.timeline] || tmap.data.timeline;
    return tmap;
  });
}

function updateHotspots(hotspots, namespaceMap) {
  return (hotspots || []).map((hotspot) => {
    if (hotspot.objectId) {
      hotspot.objectId = RenamespaceUtils.renamespaceValue(hotspot.objectId, namespaceMap);
    }
    return hotspot;
  });
}

function updateColorRecipes(colorRecipeObjects, namespaceMap) {
  if (colorRecipeObjects) {
    return {
      xray: mapKeys(colorRecipeObjects.xray, (value, key) =>
        RenamespaceUtils.renamespaceValue(key, namespaceMap),
      ),
      highlight: mapKeys(colorRecipeObjects.highlight, (value, key) =>
        RenamespaceUtils.renamespaceValue(key, namespaceMap),
      ),
    };
  }

  return {};
}

function updateVisibilityLayers(visibilityLayers, namespaceMap) {
  const updateVisibilityLayer = (visibilityLayer: VisibilityLayerModel): VisibilityLayerModel => {
    return {
      ...visibilityLayer,
      objectIds: visibilityLayer.objectIds.map((objectId) => {
        return RenamespaceUtils.renamespaceValue(objectId, namespaceMap);
      }),
    };
  };

  const updatedVisibilityLayers = { ...visibilityLayers };

  if (Array.isArray(visibilityLayers.layers)) {
    updatedVisibilityLayers.layers = visibilityLayers.layers.map(updateVisibilityLayer);
  }

  return updatedVisibilityLayers;
}

function updateChapters(chapters: ChapterModel[], namespaceMap) {
  return chapters.map((chapter) => {
    return {
      ...chapter,
      ...updateProps(chapter, namespaceMap),
      colorRecipeObjects: updateColorRecipes((chapter as any).colorRecipeObjects, namespaceMap),
      timelineMap: updateTimelineMap(chapter.timelineMap, namespaceMap),
      labels: updateLabels(chapter.labels, namespaceMap),
      hotspots: updateHotspots(chapter.hotspots, namespaceMap),
      visibilityLayers: updateVisibilityLayers(chapter.visibilityLayers, namespaceMap),
    };
  });
}

function updateStateId(
  stateId: string | string[],
  namespaceMap: { [original: string]: string },
): string[] {
  // handle the unfortunate case where stateId is a string, instead of a string[]
  if (typeof stateId === 'string') {
    return [namespaceMap[stateId] || stateId];
  }

  return stateId.map((oldState) => {
    return namespaceMap[oldState] || oldState;
  });
}

function updateSceneGraph(sceneGraph: SceneGraphNode[], namespaceMap) {
  for (const node of sceneGraph) {
    ['objectId', 'displayName', 'geometryId', 'morphId', 'materialId', 'transformId'].forEach(
      (assetType) => {
        if (node[assetType]) {
          node[assetType] = RenamespaceUtils.renamespaceValue(node[assetType], namespaceMap);
        }
      },
    );

    updateSceneGraph(node.objects || [], namespaceMap);
  }

  return sceneGraph;
}

/**
 * Renamespace all properties that reference the previous moduleId.
 * Check renamespace.spec for a full list of effected properties.
 */

export function renamespace(story: StoryModel, namespaceMap): StoryModel {
  const stateId = updateStateId(story.stateId, namespaceMap);

  // asset libs
  const tweenLibs = story.assetLibraries.tweens.map((t) =>
    RenamespaceUtils.renamespaceValue(t, namespaceMap),
  );
  const reflectionLibs = story.assetLibraries.reflections.map((r) =>
    RenamespaceUtils.renamespaceValue(r, namespaceMap),
  );

  // reflections
  const reflections = mapValues(story.reflections, (value, key) =>
    RenamespaceUtils.renamespaceValue(value, namespaceMap),
  );

  const chapters = updateChapters(story.chapters, namespaceMap);

  const sceneGraph = updateSceneGraph(story.sceneGraph as SceneGraphNode[], namespaceMap);

  return {
    ...story,
    stateId,
    ...updateProps(story, namespaceMap),
    assetLibraries: {
      ...story.assetLibraries,
      tweens: tweenLibs,
      reflections: reflectionLibs,
    },
    reflections,
    chapters,
    sceneGraph,
  };
}
